import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './StomperTrucks.css';
import f600 from '../images/f600.jpg';
import f600b from '../images/f600b.jpg';
import f600c from '../images/f600c.jpg';

const HERO_IMG = f600;
const GALLERY = [f600, f600b, f600c];

const FEATURES = [
    {
        num: '01',
        title: 'Ford F-450, F-550 & F-600',
        body: 'We build exclusively on the F-450, F-550, and F-600 — the heavy-duty end of the Super Duty lineup. These are the platforms with the payload capacity and chassis strength to do this conversion right.',
    },
    {
        num: '02',
        title: 'Air Suspension',
        body: 'Full air-ride lift with adjustable height on demand. Lower it to the dock, raise it for the terrain. The ride quality speaks for itself — smooth on the highway, capable everywhere else.',
    },
    {
        num: '03',
        title: 'Super Single Conversion',
        body: 'Factory duals come off. Wide-base super singles go on. The result is a wider stance, better traction, and a cleaner profile. One tire per side — less maintenance, more capability.',
    },
    {
        num: '04',
        title: 'Custom Flatbed',
        body: 'Every flatbed is fabricated to your spec. Steel-welded, powder-coated, fitted with integrated D-rings and full LED lighting. Tell us what you haul — we\'ll build the bed around the job.',
    },
];

const SPECS = [
    {
        group: 'Platform',
        rows: [
            ['Base Vehicle', 'Ford F-450, F-550, F-600'],
            ['Class', 'Class 4 – Class 6'],
            ['GVWR', 'Up to 22,000 lbs'],
            ['Engine (Gas)', '7.3L V8 Godzilla'],
            ['Engine (Diesel)', '6.7L Power Stroke V8'],
        ],
    },
    {
        group: 'Suspension & Wheels',
        rows: [
            ['Suspension', 'Kelderman Air Ride Lift'],
            ['Rear Tires', 'Buckstop Super Singles'],
            ['Configuration', 'Single Rear Wheel (converted)'],
            ['Ride Adjust', 'Air — raise / lower on demand'],
        ],
    },
    {
        group: 'Body',
        rows: [
            ['Bed Style', 'Custom Steel Flatbed'],
            ['Lighting', 'Full LED'],
            ['Tie-Downs', 'Integrated D-Rings'],
            ['Finish', 'Powder coat — color to spec'],
        ],
    },
];

const StomperTrucks = () => {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        document.documentElement.classList.add('sub-page');
        return () => document.documentElement.classList.remove('sub-page');
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <div className="st-page">
            {/* ── Nav ── */}
            <nav className={`st-nav${scrolled ? ' st-nav--scrolled' : ''}`}>
                <div className="st-nav-inner">
                    <div className="st-logo">
                        <span className="st-logo-main">STOMPER</span>
                        <span className="st-logo-sub">TRUCKS</span>
                    </div>
                    <div className="st-nav-links">
                        <a href="#builds">Builds</a>
                        <a href="#specs">Specs</a>
                        <a href="#contact">Contact</a>
                    </div>
                    <Link to="/" className="st-back-link">← Portfolio</Link>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section className="st-hero">
                <div className="st-hero-bg" style={{ backgroundImage: `url(${HERO_IMG})` }} />
                <div className="st-hero-content">
                    <p className="st-eyebrow">Built for One Purpose</p>
                    <h1 className="st-hero-h1">STOMP<br />HARDER.</h1>
                    <p className="st-hero-sub">
                        StomperTrucks.com specializes in building the world's most capable super single conversions. Our trucks are intended to be used as overland vehicles, and accept any modern pickup camper.
                    </p>
                    <a href="#contact" className="st-btn">Get Your Build</a>
                </div>
            </section>

            {/* ── Features ── */}
            <section className="st-features" id="builds">
                <div className="st-container">
                    <p className="st-eyebrow">Every Build Includes</p>
                    <h2 className="st-section-h2">THE COMPLETE PACKAGE</h2>
                    <div className="st-features-grid">
                        {FEATURES.map(f => (
                            <div className="st-feature-card" key={f.num}>
                                <span className="st-feature-num">{f.num}</span>
                                <h3 className="st-feature-title">{f.title}</h3>
                                <p className="st-feature-body">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Gallery ── */}
            <section className="st-gallery">
                {GALLERY.map((src, i) => (
                    <div
                        key={i}
                        className="st-gallery-img"
                        style={{ backgroundImage: `url(${src})` }}
                    />
                ))}
            </section>

            {/* ── Specs ── */}
            <section className="st-specs" id="specs">
                <div className="st-container">
                    <p className="st-eyebrow">Under the Hood</p>
                    <h2 className="st-section-h2">STANDARD SPECS</h2>
                    <div className="st-specs-grid">
                        {SPECS.map(g => (
                            <div className="st-spec-group" key={g.group}>
                                <h4 className="st-spec-group-title">{g.group}</h4>
                                {g.rows.map(([label, val]) => (
                                    <div className="st-spec-row" key={label}>
                                        <span className="st-spec-label">{label}</span>
                                        <span className="st-spec-val">{val}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="st-cta" id="contact">
                <div className="st-container st-cta-inner">
                    <h2 className="st-cta-h2">READY TO ORDER?</h2>
                    <p className="st-cta-sub">
                        Every truck is built to order. Reach out to talk spec,
                        pricing, and lead time — we'll get you dialed in.
                    </p>
                    <a href="mailto:info@stompertrucks.com" className="st-btn st-btn--dark">
                        info@stompertrucks.com
                    </a>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="st-footer">
                <span>© 2025 Stomper Trucks</span>
                <Link to="/" className="st-back-link">Built by Roland Forbes</Link>
            </footer>
        </div>
    );
};

export default StomperTrucks;
